import type { TreeNode } from '@morph-mapper/types';
import type { Result } from 'neverthrow';
import type { FieldSuggestion, PluginFrameworkError } from './framework';

/**
 * Stages run in this order, so a later stage can build on an earlier one
 * (e.g. compose a date and a time before the date format is picked).
 */
export const TRANSFORM_STAGES = [
  'compose',
  'split',
  'cleanup',
  'number',
  'date',
] as const;

export type TransformStage = (typeof TRANSFORM_STAGES)[number];

/**
 * A snippet and the value it is expected to render to.
 */
export type TransformState = {
  snippet: unknown;
  value: unknown;
};

export type TransformContext = {
  entry: TreeNode;
  /** The suggestion the chain started from. */
  suggestion: FieldSuggestion;
  /** All suggestions the chain runs on, e.g. to confirm a split part. */
  suggestions: FieldSuggestion[];
  /**
   * What the suggestion's snippet renders to in every row, when the field
   * sits under an iterator. One snippet runs on all rows, so a step can use
   * this to pick a transform that fits each of them.
   */
  rows?: unknown[];
};

/**
 * One transformation a plugin contributes to the chain. `apply` predicts
 * the new value locally; the framework verifies it by rendering once at
 * the end of the chain.
 *
 * - `replace`: the result takes the place of its input (e.g. a number
 *   conversion).
 * - `add`: the result is offered next to its input (e.g. a split part).
 */
export type TransformStep = {
  name: string;
  stage: TransformStage;
  mode: 'add' | 'replace';
  apply: (state: TransformState, ctx: TransformContext) => TransformState[];
  /**
   * Optional filter on the incoming suggestions: returning false drops the
   * suggestion before the chain runs (e.g. a date format that does not fit
   * the date text).
   */
  keep?: (ctx: TransformContext) => boolean;
};

export type TransformRenderFn = (
  code: string,
) => Promise<Result<unknown, PluginFrameworkError>>;
